import { useTranslation } from 'react-i18next';

import EmptyState from '@/components/ui/EmptyState';
import SectionHeader from '@/components/ui/SectionHeader';

interface Breakdown {
  title: string;
  overall_tone: string;
  scenes: {
    scene_number: number;
    description: string;
    location: string;
    time_of_day: string;
    mood: string;
    dialog_lines: { id: string; speaker: string; text: string }[];
    vo_lines: { id: string; text: string; speaker?: string }[];
  }[];
}

/**
 * How the crew read the screenplay: scenes, each tagged with a mood and a time of
 * day. Those tags are what keep tone consistent across every shot, so this is the
 * first place to look if the film comes out feeling wrong.
 */
export default function ScriptBreakdownCard({ breakdown }: { breakdown: unknown }) {
  const { t } = useTranslation('production');
  const data = breakdown as Breakdown | null;

  if (!data?.scenes?.length) {
    return <EmptyState title={t('breakdown.emptyTitle')}>{t('breakdown.empty')}</EmptyState>;
  }

  return (
    <div>
      <SectionHeader title={t('step.breakdown')} help={t('breakdown.help')} subtitle={data.overall_tone} />
      <div className="space-y-3.5">
        {data.scenes.map((scene) => (
          <div key={scene.scene_number} className="rounded-lg border border-border bg-elevated p-4">
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
              <b className="font-display text-[13.5px] font-semibold">
                {t('breakdown.scene', { number: scene.scene_number, location: scene.location })}
              </b>
              <div className="flex flex-wrap gap-1.5">
                {[scene.mood, scene.time_of_day].filter(Boolean).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-border bg-card px-2.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">{scene.description}</p>
            {scene.dialog_lines?.map((dl) => (
              <div key={dl.id} className="mt-2 text-[12.5px] text-muted-foreground">
                <b className="font-semibold text-faint">{dl.speaker}:</b> {dl.text}
              </div>
            ))}
            {scene.vo_lines?.map((vo) => (
              <div key={vo.id} className="mt-2 text-[12.5px] italic text-muted-foreground">
                <b className="not-italic font-semibold text-faint">{vo.speaker ? `${vo.speaker} (V.O.)` : 'V.O.'}:</b> {vo.text}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
